import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';
import { catchError, map, Observable, of, shareReplay, tap } from 'rxjs';
import { withErrorHandling } from '../../shared/services/decorators/with-error-handling';
import { ContractMapEntry } from '../models/contract-map.model';
import { ContractMaster } from '../models/contract-master.model';
import { ContractRequest } from '../models/contract-request.model';
import { ApiResponse, ContractResponse } from '../models/contract.model';
import { FacilityPayorPlanMap } from '../models/facility-payor-plan-map.model';
import { HealthSystem } from '../models/health-system.model';
import { ScheduleDetectionLink, ScheduleDetectionLinkInput } from '../models/schedule-detection-link.model';
import { TestRunStatus } from '../models/test-run.model';
import { PRICING_WORKFLOW_API_ROOT } from './test-run-line-items.service';

export type SettableWorkflowStage = 'Approved' | 'InProgress' | 'PlanCodeMappedNotApproved';

// Injection tokens for extensibility
export interface IContractService {
  getAllContracts(healthSystemId: string): Observable<ApiResponse<ContractResponse[]>>;
  getContractDetailsById(contractId: number | string): Observable<ApiResponse<any>>;
  createContract(contract: ContractRequest): Observable<ApiResponse<ContractResponse>>;
  updateContract(contractId: number | string, contract: ContractRequest): Observable<ApiResponse<ContractResponse>>;
  deleteContract(contractId: number | string): Observable<ApiResponse<any>>;
  exportContract(contractId: number | string, status?: 'InProgress' | 'Approved'): Observable<ApiResponse<any>>;
  getImportWorkflow(contractId: string): Observable<ApiResponse<any>>;
  getLatestContractIdByMasterId(contractMasterId: string): Observable<number>;
  setContractWorkflowStage(
    contractMasterId: number | string,
    workflowStage: SettableWorkflowStage
  ): Observable<ApiResponse<{ contractId: number | null; workflowStage: string | null }>>;
  captureApprovedPreEditSnapshot(contractId: number | string): Observable<ApiResponse<any>>;
  restoreApprovedPreEditSnapshot(contractId: number | string): Observable<ApiResponse<any>>;
  getLatestWorkflowStageByMasterId(
    contractMasterId: string
  ): Observable<ApiResponse<{ contractId: number | null; workflowStage: string | null }>>;
  getContractsByMaster(contractMasterId: string): Observable<any[]>;
  initiateTestRun(contractId: number): Observable<ApiResponse<any>>;
  getTestRunStatus(contractId: number, healthSystemId: string): Observable<TestRunStatus[]>;
  getPayerPlanMapsByHealthSystemId(
    healthSystemId: string,
    mapByFacilityCode: boolean | null
  ): Observable<FacilityPayorPlanMap[]>;
  getContractMapByContractMasterId(contractMasterId: number): Observable<ContractMapEntry[]>;
  mapPayerPlans(
    contractMasterId: number,
    facilityPayorPlanMapIds: number[]
  ): Observable<ApiResponse<FacilityPayorPlanMap[]>>;
  unmapPayerPlans(contractMapIds: number[]): Observable<any>;
  getAccessibleHealthSystems(): Observable<HealthSystem[]>;
  getPhysicianHealthSystems(): Observable<HealthSystem[]>;
  getOnboardedPbHealthSystems(): Observable<HealthSystem[]>;
  getContractMasters(healthSystemId: string): Observable<ContractMaster[]>;
  getDetectionLinks(contractMasterId: number): Observable<ScheduleDetectionLink[]>;
  saveDetectionLinks(
    contractMasterId: number,
    links: ScheduleDetectionLinkInput[]
  ): Observable<ApiResponse<ScheduleDetectionLink[]>>;
  removeDetectionLink(contractMasterId: number, alternateContractMasterId: number): Observable<ApiResponse<any>>;
  clearPayerPlanMapCache(healthSystemId?: string): void;
  clearContractMastersCache(healthSystemId?: string): void;
}

export const CONTRACT_SERVICE = new InjectionToken<IContractService>('CONTRACT_SERVICE');
export const PHYSICIAN_CONTRACTS_BASE_URL = new InjectionToken<string>('PHYSICIAN_CONTRACTS_BASE_URL');
export const CONTRACTS_BASE_URL = new InjectionToken<string>('CONTRACTS_BASE_URL');

@Injectable({
  providedIn: 'root',
})
export class ContractService implements IContractService {
  private readonly payerPlanMapCache = new Map<string, Observable<FacilityPayorPlanMap[]>>();
  private readonly contractMastersCache = new Map<string, Observable<ContractMaster[]>>();
  private healthSystemsCache$: Observable<HealthSystem[]> | null = null;
  private pbHealthSystemsCache$: Observable<HealthSystem[]> | null = null;

  constructor(
    private readonly http: HttpClient,
    @Inject(PHYSICIAN_CONTRACTS_BASE_URL) private readonly physicianContractApi: string,
    @Inject(CONTRACTS_BASE_URL) private readonly contractApi: string,
    @Optional() @Inject(PRICING_WORKFLOW_API_ROOT) private readonly pricingWorkflowApiRoot: string | null = null
  ) {}

  getAllContracts(healthSystemId: string): Observable<ApiResponse<ContractResponse[]>> {
    const url = `${this.physicianContractApi}/listing?healthSystemId=${encodeURIComponent(healthSystemId)}`;
    return withErrorHandling(this.http.get<ApiResponse<ContractResponse[]>>(url), 'getAllContracts');
  }

  // Physician clients (pricingdb.HealthSystem ProviderTypeId = 2). Not user-scoped.
  getPhysicianHealthSystems(): Observable<HealthSystem[]> {
    return withErrorHandling(
      this.http
        .get<ApiResponse<HealthSystem[]>>(`${this.physicianContractApi}/health-systems`)
        .pipe(map((res) => (Array.isArray(res?.data) ? res.data : []))),
      'getPhysicianHealthSystems'
    );
  }

  getContractDetailsById(contractId: number | string): Observable<ApiResponse<any>> {
    return withErrorHandling(
      this.http.get<ApiResponse<any>>(`${this.physicianContractApi}/${contractId}/details`),
      'getContractDetailsById'
    );
  }

  createContract(contract: ContractRequest): Observable<ApiResponse<ContractResponse>> {
    return withErrorHandling(
      this.http.post<ApiResponse<ContractResponse>>(this.physicianContractApi, contract),
      'createContract'
    );
  }

  updateContract(contractId: number | string, contract: ContractRequest): Observable<ApiResponse<ContractResponse>> {
    return withErrorHandling(
      this.http.put<ApiResponse<ContractResponse>>(`${this.physicianContractApi}/${contractId}`, contract),
      'updateContract'
    );
  }

  deleteContract(contractId: number | string): Observable<ApiResponse<any>> {
    return withErrorHandling(
      this.http.delete<ApiResponse<any>>(`${this.physicianContractApi}/${contractId}`),
      'deleteContract'
    );
  }

  exportContract(
    contractId: number | string,
    status: 'InProgress' | 'Approved' = 'InProgress'
  ): Observable<ApiResponse<any>> {
    const url = `${this.physicianContractApi}/${contractId}/exporttoblob?contractWorkflowStage=${encodeURIComponent(status)}`;
    return withErrorHandling(this.http.post<ApiResponse<any>>(url, {}), 'exportContract');
  }

  setContractWorkflowStage(
    contractMasterId: number | string,
    workflowStage: SettableWorkflowStage
  ): Observable<ApiResponse<{ contractId: number | null; workflowStage: string | null }>> {
    const url = `${this.contractApi}/Contract/set-workflow-stage/${encodeURIComponent(String(contractMasterId))}?workflowStage=${encodeURIComponent(workflowStage)}`;
    return withErrorHandling(
      this.http.post<ApiResponse<{ contractId: number | null; workflowStage: string | null }>>(url, {}),
      'setContractWorkflowStage'
    );
  }

  captureApprovedPreEditSnapshot(contractId: number | string): Observable<ApiResponse<any>> {
    const url = `${this.physicianContractApi}/${contractId}/approved-pre-edit-snapshot`;
    return withErrorHandling(this.http.post<ApiResponse<any>>(url, {}), 'captureApprovedPreEditSnapshot');
  }

  restoreApprovedPreEditSnapshot(contractId: number | string): Observable<ApiResponse<any>> {
    const url = `${this.physicianContractApi}/${contractId}/restore-approved-pre-edit-snapshot`;
    return withErrorHandling(this.http.post<ApiResponse<any>>(url, {}), 'restoreApprovedPreEditSnapshot');
  }

  getImportWorkflow(contractId: string): Observable<ApiResponse<any>> {
    const includeWorkflowDetails = true;
    const includeWorkflowErrorDetails = true;
    const sourceSystem = 'pb-up';
    const url = `${this.contractApi}/import/workflow/search?SourceContractId=${contractId}&sourceSystem=${sourceSystem}&includeWorkflowDetails=${includeWorkflowDetails}&includeWorkflowErrorDetails=${includeWorkflowErrorDetails}`;
    return withErrorHandling(this.http.get<ApiResponse<any>>(url), 'getImportWorkflow');
  }

  getLatestContractIdByMasterId(contractMasterId: string): Observable<number> {
    const url = `${this.contractApi}/Contract/latest-by-master/${encodeURIComponent(contractMasterId)}`;
    return withErrorHandling(this.http.get<number>(url), 'getLatestContractIdByMasterId');
  }

  getLatestWorkflowStageByMasterId(
    contractMasterId: string
  ): Observable<ApiResponse<{ contractId: number | null; workflowStage: string | null }>> {
    const url = `${this.physicianContractApi}/by-master/${encodeURIComponent(contractMasterId)}/workflow-stage`;
    return withErrorHandling(
      this.http.get<ApiResponse<{ contractId: number | null; workflowStage: string | null }>>(url),
      'getLatestWorkflowStageByMasterId'
    );
  }

  // Returns every Contract row tied to the master. The API's ContractRevision endpoint
  // takes (masterId, contractId) and filters out the row matching contractId — passing 0
  // (an id no real contract will have) yields the full set.
  getContractsByMaster(contractMasterId: string): Observable<any[]> {
    const url = `${this.contractApi}/ContractRevision/${encodeURIComponent(contractMasterId)}/0`;
    return withErrorHandling(this.http.get<any[]>(url), 'getContractsByMaster');
  }

  initiateTestRun(contractId: number): Observable<ApiResponse<any>> {
    const url = `${this.contractApi}/Contract/testrun/${encodeURIComponent(contractId)}`;
    return withErrorHandling(this.http.get<ApiResponse<any>>(url), 'initiateTestRun');
  }

  getTestRunStatus(contractId: number, healthSystemId: string): Observable<TestRunStatus[]> {
    const url = `${this.contractApi}/TestRun/${encodeURIComponent(contractId)}?healthSystemId=${encodeURIComponent(healthSystemId)}`;
    return withErrorHandling(this.http.get<TestRunStatus[]>(url), 'getTestRunStatus');
  }

  getPayerPlanMapsByHealthSystemId(
    healthSystemId: string,
    mapByFacilityCode: boolean | null
  ): Observable<FacilityPayorPlanMap[]> {
    if (!healthSystemId?.trim()) {
      return of([]);
    }

    const trimmedId = healthSystemId.trim();
    const key = `${trimmedId}|${mapByFacilityCode ?? 'all'}`;
    const cached = this.payerPlanMapCache.get(key);
    if (cached) {
      return cached;
    }

    const flagParam = mapByFacilityCode == null ? '' : `&mapByFacilityCode=${mapByFacilityCode}`;
    const url = `${this.physicianContractApi}/payer-plan-map?healthSystemId=${encodeURIComponent(trimmedId)}${flagParam}`;
    const req$ = this.http.get<ApiResponse<FacilityPayorPlanMap[]>>(url).pipe(
      map((res) => (Array.isArray(res?.data) ? res.data : [])),
      tap({ error: () => this.payerPlanMapCache.delete(key) }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.payerPlanMapCache.set(key, req$);
    return req$;
  }

  getContractMapByContractMasterId(contractMasterId: number): Observable<ContractMapEntry[]> {
    const url = `${this.contractApi}/ContractMap/${encodeURIComponent(contractMasterId)}`;
    return withErrorHandling(
      this.http.get<ContractMapEntry[]>(url).pipe(map((list) => (Array.isArray(list) ? list : []))),
      'getContractMapByContractMasterId'
    );
  }

  mapPayerPlans(
    contractMasterId: number,
    facilityPayorPlanMapIds: number[]
  ): Observable<ApiResponse<FacilityPayorPlanMap[]>> {
    // No withErrorHandling wrapper — it rethrows a plain Error and strips the HttpErrorResponse,
    // which would hide the 400 status + server-provided invariant message from the component.
    const url = `${this.contractApi}/ContractMap/map/${encodeURIComponent(contractMasterId)}`;
    return this.http.post<ApiResponse<FacilityPayorPlanMap[]>>(url, facilityPayorPlanMapIds);
  }

  unmapPayerPlans(contractMapIds: number[]): Observable<any> {
    const url = `${this.contractApi}/ContractMap`;
    return withErrorHandling(this.http.delete<any>(url, { body: contractMapIds }), 'unmapPayerPlans');
  }

  getAccessibleHealthSystems(): Observable<HealthSystem[]> {
    if (this.healthSystemsCache$) {
      return this.healthSystemsCache$;
    }

    this.healthSystemsCache$ = this.http.get<HealthSystem[]>(`${this.contractApi}/HealthSystems`).pipe(
      map((list) => (Array.isArray(list) ? list : [])),
      catchError(() => of([])),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    return this.healthSystemsCache$;
  }

  // Clients ONBOARDED to PB only — same source the pricing-ui test-population PB dropdown uses:
  // pricing-workflow-api GET /api/HealthSystems?providerType=Physician (filters to onboarded +
  // Physician provider type). Used by the standalone Payer Plan Mapping page's Client dropdown.
  getOnboardedPbHealthSystems(): Observable<HealthSystem[]> {
    if (this.pbHealthSystemsCache$) {
      return this.pbHealthSystemsCache$;
    }

    const root = this.pricingWorkflowApiRoot;
    if (!root) {
      return of([]);
    }

    this.pbHealthSystemsCache$ = this.http
      .get<HealthSystem[]>(`${root}/HealthSystems?providerType=Physician`)
      .pipe(
        map((list) => (Array.isArray(list) ? list : [])),
        catchError(() => of([])),
        shareReplay({ bufferSize: 1, refCount: true })
      );

    return this.pbHealthSystemsCache$;
  }

  // The mappable contract masters for a health system — any PB contract exported at least
  // once (and thus carrying a contractMasterId). Drives the standalone page's contract picker.
  getContractMasters(healthSystemId: string): Observable<ContractMaster[]> {
    if (!healthSystemId?.trim()) {
      return of([]);
    }

    const key = healthSystemId.trim();
    const cached = this.contractMastersCache.get(key);
    if (cached) {
      return cached;
    }

    const url = `${this.physicianContractApi}/contract-masters?healthSystemId=${encodeURIComponent(key)}`;
    const req$ = this.http.get<ApiResponse<ContractMaster[]>>(url).pipe(
      map((res) => (Array.isArray(res?.data) ? res.data : [])),
      tap({ error: () => this.contractMastersCache.delete(key) }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.contractMastersCache.set(key, req$);
    return req$;
  }

  // Cross-contract schedule-detection links (ADO 293059). The source master's links to
  // alternate masters that detection should also evaluate.
  getDetectionLinks(contractMasterId: number): Observable<ScheduleDetectionLink[]> {
    const url = `${this.physicianContractApi}/${contractMasterId}/detection-links`;
    return withErrorHandling(
      this.http
        .get<ApiResponse<ScheduleDetectionLink[]>>(url)
        .pipe(map((res) => (Array.isArray(res?.data) ? res.data : []))),
      'getDetectionLinks'
    );
  }

  // Replace-set save: the full set of links is sent on every call.
  saveDetectionLinks(
    contractMasterId: number,
    links: ScheduleDetectionLinkInput[]
  ): Observable<ApiResponse<ScheduleDetectionLink[]>> {
    const url = `${this.physicianContractApi}/${contractMasterId}/detection-links`;
    // The API binds a wrapper request model (SaveScheduleDetectionLinksRequestDto { Links }),
    // so the body must be { links: [...] } — NOT a bare array (which fails model binding).
    return withErrorHandling(
      this.http.put<ApiResponse<ScheduleDetectionLink[]>>(url, { links }),
      'saveDetectionLinks'
    );
  }

  removeDetectionLink(
    contractMasterId: number,
    alternateContractMasterId: number
  ): Observable<ApiResponse<any>> {
    const url = `${this.physicianContractApi}/${contractMasterId}/detection-links/${alternateContractMasterId}`;
    return withErrorHandling(this.http.delete<ApiResponse<any>>(url), 'removeDetectionLink');
  }

  clearPayerPlanMapCache(healthSystemId?: string): void {
    if (healthSystemId) {
      const prefix = `${healthSystemId.trim()}|`;
      for (const key of Array.from(this.payerPlanMapCache.keys())) {
        if (key.startsWith(prefix)) {
          this.payerPlanMapCache.delete(key);
        }
      }
    } else {
      this.payerPlanMapCache.clear();
    }
  }

  clearContractMastersCache(healthSystemId?: string): void {
    if (healthSystemId) {
      this.contractMastersCache.delete(healthSystemId.trim());
    } else {
      this.contractMastersCache.clear();
    }
  }
}
